import { Box, BoxStatus, Flexbox, Heading, CodeIcon, Body } from "codekit";
import { useGetDataGraphicTransportSinDoQuery } from "../../../../libs/redux/slices/Graficsx/Doctrado/doctradoApiSlice";
import { useGetUrlRedirectCimaMutation } from "../../../../libs/redux/slices/Graficsx/graficsxApiSlice";

const Doctrado = ({ graphName }) => {
  const { data, isLoading, isError } = useGetDataGraphicTransportSinDoQuery();
  const [getUrlRedirectCima] = useGetUrlRedirectCimaMutation();

  /**
   * Calcula una fecha pasada restando días a la fecha actual.
   * @param {number} days - Días a restar
   * @returns {string} Fecha en formato YYYY-MM-DD
   */
  const getDateAgo = (days) => {
    const d = new Date();
    d.setDate(d.getDate() - days);
    return d.toISOString().split("T")[0];
  };

  /**
   * Redirige al tracking de Doc. Transporte con el filtro de rango seleccionado.
   * @param {string} rangeKey - Clave del rango (range1, range2, range3)
   */
  const handleRedirect = async (rangeKey) => {
    const rangeFilters = {
      range1: { [`filters[0][DTRCYYYY.REGFECXX][BETWEEN]`]: [getDateAgo(60), getDateAgo(30)] },
      range2: { [`filters[0][DTRCYYYY.REGFECXX][BETWEEN]`]: [getDateAgo(89), getDateAgo(61)] },
      range3: { [`filters[0][DTRCYYYY.REGFECXX][BETWEEN]`]: [getDateAgo(90), getDateAgo(90)] },
    };

    const payload = {
      MODIDXXX: "113",
      PROIDXXX: "100",
      BANEJEFU: "SI",
      NOMFUNXX: "handleToggleFilter",
      SELECTOR: {},
      ...(rangeFilters[rangeKey] || {}),
    };

    await getUrlRedirectCima(payload)
      .unwrap()
      .then((response) => {
        if (response.data) {
          const token = response.data.split("/").pop();
          window.open(`${window.location.origin}/external/${token}`, "_blank");
        }
      })
      .catch((err) => console.error("Error al redirigir:", err));
  };

  if (isLoading) return <BoxStatus variant="loading" text="Cargando información del dashboard..." />;
  if (isError) return <BoxStatus variant="error" text="Error al cargar la información del dashboard" />;

  const dashboardData = data?.data || {};
  const totalCount = dashboardData.total || 0;
  const ranges = dashboardData.ranges || {};

  const capitalize = (str) => str.charAt(0).toUpperCase() + str.slice(1);

  const currentDateFormatted = new Intl.DateTimeFormat("es-ES", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  }).format(new Date());

  return (
    <Box
      bg="neutral-100"
      rounded="lg"
      style={{
        width: "calc(100% - 48px)",
        maxWidth: "1230px",
        display: "flex",
        flexDirection: "column",
        margin: "24px 0 24px 24px",
        height: "auto",
        boxSizing: "border-box",
        alignSelf: "start",
        overflowX: "hidden",
        border: "1px solid #e0e0e0",
        padding: "24px",
        backgroundColor: "#f5f5f5",
      }}
    >
      <Box mb={2}>
        <Flexbox justifyContent="between" alignItems="center">
          <Box>
            <Heading
              variant="heading-4"
              color="neutral-900"
              weight="bold"
              uppercase
              mb={0}
              style={{ fontSize: "18px" }}
            >
              {graphName || "DOC. TRANSPORTE ACTIVOS SIN DO ASOCIADO"}
            </Heading>
            <Body variant="body-s" color="neutral-500" style={{ fontSize: "12px" }}>
              {capitalize(currentDateFormatted)}
            </Body>
          </Box>
        </Flexbox>
      </Box>

      <Box mb={4}>
        <div style={{ display: "flex", alignItems: "baseline", gap: "16px", flexWrap: "wrap" }}>
          <span
            style={{
              fontSize: "110px",
              fontWeight: "100",
              lineHeight: "1",
              letterSpacing: "-5px",
              color: "#111827",
              fontFamily: "sans-serif",
            }}
          >
            {totalCount}
          </span>
          <Box style={{ paddingBottom: "16px" }}>
            <Body variant="body-m" color="neutral-500" style={{ lineHeight: "1", fontWeight: "400", fontSize: "16px" }}>
              Doc. de transporte activos
            </Body>
            <Body variant="body-s" color="neutral-500" style={{ lineHeight: "1", fontSize: "16px" }}>
              sin DO por asociar
            </Body>
          </Box>
        </div>
      </Box>

      <Box mb={8}>
        <div
          style={{
            height: "16px",
            width: "100%",
            display: "flex",
            borderRadius: "4px",
            overflow: "hidden",
            backgroundColor: "#e5e7eb",
          }}
        >
          {Object.entries(ranges).map(([key, range]) => {
            const colorMap = { success: "#16a34a", warning: "#d97706", error: "#dc2626" };
            return (
              <div
                key={key}
                style={{
                  width: `${range.percentage}%`,
                  height: "100%",
                  backgroundColor: colorMap[range.color] || "#9ca3af",
                  transition: "width 0.5s ease",
                }}
              />
            );
          })}
        </div>
      </Box>

      <div style={{ display: "flex", gap: "24px", flexWrap: "wrap", width: "100%" }}>
        {Object.entries(ranges).map(([key, range]) => (
          <Box
            spacing={4}
            key={key}
            bg={`${range.color}-100`}
            rounded="xl"
            style={{
              flex: "1 1 0px",
              minWidth: "280px",
              display: "flex",
              flexDirection: "column",
              transition: "all 0.3s ease",
              cursor: "pointer",
            }}
            p={8}
            onClick={() => handleRedirect(key)}
          >
            <Body variant="body-xs" color={`${range.color}-800`} weight="bold" uppercase mb={4}>
              {range.label}
            </Body>

            <span
              style={{
                fontSize: "64px",
                fontWeight: "100",
                lineHeight: "1",
                color: range.color === "success" ? "#166534" : range.color === "warning" ? "#9a3412" : "#991b1b",
              }}
            >
              {range.count}
            </span>

            <Body variant="body-m" color={`${range.color}-800`} mb={8} style={{ fontSize: "16px" }}>
              {range.days}
            </Body>

            <Box mb={5}>
              <Box width="100%" height={6} bg={`${range.color}-200`} rounded="full" style={{ overflow: "hidden" }}>
                <Box width={`${range.percentage}%`} height="100%" bg={`${range.color}-700`} rounded="full" />
              </Box>
            </Box>

            <Flexbox justifyContent="between" alignItems="center">
              <Box px={3} py={1} rounded="xs" bg={`${range.color}-200`}>
                <Body variant="body-s" color={`${range.color}-800`} weight="bold">
                  {range.percentage}%
                </Body>
              </Box>
              <Box color={`${range.color}-700`}>
                <CodeIcon variant="arrow-right" width={14} />
              </Box>
            </Flexbox>
          </Box>
        ))}
      </div>
    </Box>
  );
};

export default Doctrado;
